import * as React from 'react'
import cx from 'classnames'
import { ControlStatus } from '../ControlStatus'
import DatePicker from 'react-date-picker'
import Icon, { IconName } from 'components/Icon'
import { IconButton } from 'components/IconButton'
import { NEVER, Observable } from 'rxjs'
import { useFocusTracking, useObservableFocus } from 'hooks/focus-hooks'

const DateTimePicker = require('react-datetime-picker').default
const styles = require('./DateInput.sass')

export interface DateInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'value' | 'onChange'
  > {
  status?: ControlStatus
  value?: Date | null
  includeTime?: boolean
  minDate?: Date
  maxDate?: Date
  onChange?: (value: Date, e: React.ChangeEvent<HTMLInputElement>) => unknown
  dataTest?: string
  icon?: IconName
  compact?: boolean
  focus$?: Observable<unknown>
  format?: string
  clearIcon?: boolean
  calendarClassName?: string
  isNotValid?: boolean
}

export default function DateInput({
  className,
  value,
  onChange,
  status = 'default',
  includeTime,
  dataTest,
  onFocus,
  onBlur,
  focus$ = NEVER,
  icon,
  format,
  compact,
  calendarClassName,
  isNotValid,
  ...props
}: DateInputProps) {
  const Element = includeTime ? DateTimePicker : DatePicker
  const elementRef = React.useRef<any>(null)
  const [focusProps, focused] = useFocusTracking({ onFocus, onBlur })

  useObservableFocus(
    focus$,
    React.useCallback(() => {
      // The component sucks, this is the easiest way to focus it.
      // The 1st element is a hidden input.
      const elem$ =
        elementRef.current?.wrapper?.current?.querySelector(
          'input:nth-child(2)',
        )
      if (!elem$) {
        console.warn(
          'Could not find input to focus for Date component. Was there a breaking change?',
        )
      } else {
        elem$.focus()
      }
    }, []),
  )
  return (
    <div
      className={cx(
        styles.input,
        props.disabled && styles.disabled,
        focused && styles.focused,
        isNotValid && styles.isNotValid,
        styles[status],
      )}
      data-test={dataTest}
    >
      <Element
        className={cx(!compact && styles.dense)}
        ref={elementRef}
        onChange={onChange}
        calendarClassName={calendarClassName}
        value={value}
        disableClock
        clearIcon={null}
        format={format ?? `yyyy/MM/dd${includeTime ? ' h:mm a' : ''}`}
        yearPlaceholder="yyyy"
        monthPlaceholder="mm"
        dayPlaceholder="dd"
        hourPlaceholder="hh"
        minutePlaceholder="mm"
        secondPlaceholder="ss"
        calendarIcon={
          // TODO: This color is from the CustomSelect component. Maybe extract this type of icon button.
          <IconButton
            style={icon ? {} : { color: '#8993a4' }}
            data-test={`${dataTest}-calendar-toggle`}
          >
            <Icon rotate={icon ? 0 : 90} name={icon ?? 'arrow'} />
          </IconButton>
        }
        {...props}
        {...focusProps}
      />
    </div>
  )
}
